import Image, { ImageLoader, ImageProps, StaticImageData } from 'next/image'
import { memo, useEffect, useState, useMemo } from 'react'
import unkown from '@/public/images/icon-unkown.png'
import clsx from 'clsx'
import styled from 'styled-components'

/**
 * 加载image的loader
 * @param src 引入路劲
 * @param width 图片宽度
 * @param quality 图片质量，默认75
 * @returns 返回loader组装的image链接地址
 */
export const imgLoader: ImageLoader = ({ src, width, quality }) => {
  return `${src}?w=${width}&q=${quality || 75}`
}

export const coinImgLoader: ImageLoader = ({ src, width, quality }) => {
  const isParJoin = src.includes('?') ? '&' : '?'
  if (src?.startsWith('http')) return `${src}${isParJoin}w=${width || 28}&q=${quality || 75}`
  if (process.env.NODE_ENV === 'development') {
    return `https://www.gate.io${src}${isParJoin}w=${width || 28}&q=${quality || 75}`
  }
  return `${src}${isParJoin}w=${width || 28}&q=${quality || 75}`
}

export interface BaseImageProps extends Omit<ImageProps, 'src' | 'alt'> {
  src?: ImageProps['src']
  coin?: string
  width?: number
  height?: number
  className?: string
  alt?: string
  gate_trade?: number
  s3_logo_url?: string
  style?: React.CSSProperties
  loading?: 'lazy' | 'eager'
  onClick?: () => void
  errorUrl?: StaticImageData
  isRadius?: boolean
}

// 创建 styled 组件
const ErrorPlaceholder = styled.div<{ width: number; height: number; isRadius: boolean }>`
  width: ${({ width }) => `${width}px`};
  height: ${({ height }) => `${height}px`};
  min-width: ${({ width }) => `${width}px`};
  border-radius: ${({ isRadius }) => (isRadius ? '50%' : '0')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ width }) => `${width / 2}px`};
  color: var(--color-error);
  background-color: var(--color-icon-1);
  font-weight: 500;
`

const BaseImage = ({
  src,
  coin = '0',
  width = 16,
  height = 16,
  alt = coin,
  errorUrl,
  isRadius,
  ...rest
}: BaseImageProps) => {
  const [imgUrl, setImgUrl] = useState(`/images/coin_icon/64/${coin?.toLowerCase() || '0'}.png`)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (src) {
      setError(false)
    }
  }, [src])

  const prefix = useMemo(() => {
    const regex = /[a-zA-Z0-9]/
    return typeof alt === 'string' ? alt?.match(regex)?.[0] || '' : ''
  }, [alt])

  if (!src || error) {
    if (errorUrl) {
      return (
        <Image
          style={isRadius ? { borderRadius: '50%' } : {}}
          loader={imgLoader}
          src={errorUrl}
          width={width}
          height={height}
          alt={alt}
          {...rest}
        />
      )
    }
    return (
      <ErrorPlaceholder
        className={clsx('base-image no-img layout-theme', rest.className)}
        width={width}
        height={height}
        isRadius={Boolean(isRadius)}
        style={rest.style}
      >
        {prefix}
      </ErrorPlaceholder>
    )
  }

  return (
    <Image
      style={isRadius ? { borderRadius: '50%' } : {}}
      loader={(src || imgUrl).toString().startsWith('/price/public') ? imgLoader : coinImgLoader}
      src={src ?? ''}
      width={width}
      height={height}
      alt={alt}
      onError={() => {
        setError(true)
        setImgUrl(`/images/coin_icon/64/0.png`)
      }}
      {...rest}
    />
  )
}

export default memo(BaseImage, (prevProps: BaseImageProps, nextProps: BaseImageProps) => {
  return (
    prevProps.src === nextProps.src &&
    prevProps.alt === nextProps.alt &&
    prevProps.coin === nextProps.coin &&
    prevProps.width === nextProps.width
  )
})
